/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, Inject, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { LTI_COURSE_UID, LTI_PROVIDER } from '../../../page-modules/lti/services/lti-course-data.service';
import { CORE_PLAYLIST_DATA_SERVICE, PlaylistDataService } from '../../../page-modules/playlist/services/create/core/data.service';
import { RedirectHelper } from '../../../page-modules/resource/store/editor/content/helpers/redirect.helper';
import { AccessTokenService } from '../../services/access-token.service';
import { USER_IS_LOGGED_IN } from '../../services/api-basic-auth.service';
import { AUTH_SERVICE, BasicAuthService } from '../../services/basic-auth.service';
import * as UserAuthActions from '../../store/user-auth.actions';
import { UserAuthState } from '../../store/user-auth.state';

@Component({
  template: '',
})
export class LtiLaunchComponent implements OnInit {
  constructor(
    private store: Store,
    private activatedRoute: ActivatedRoute,
    @Inject(AUTH_SERVICE) private authService: BasicAuthService,
    @Inject(CORE_PLAYLIST_DATA_SERVICE) private playlistDataService: PlaylistDataService,
    private router: Router,
    private ngZone: NgZone,
    private readonly accessTokenService: AccessTokenService,
  ) {
    this.accessTokenService.removeAccessToken();
  }

  ngOnInit() {
    const requestId = this.activatedRoute.snapshot.queryParamMap.get('requestId');
    if (requestId) {
      this.authService.getLtiToken(requestId, false).subscribe(
        ({ isSuccess, value }) => {
          if (isSuccess) {
            this.accessTokenService.saveAccessToken(value.access_token);
            localStorage.setItem(USER_IS_LOGGED_IN, 'true');
            this.store
              .dispatch([
                new UserAuthActions.FetchUserDetails(),
                new UserAuthActions.RedirectToAuthenticatedAppSuccess(),
                new UserAuthActions.GetHomePageTitleAndUri(),
              ])
              .toPromise()
              .then(() => {
                this.saveAdditionalParametersIntoLocalStorage();
                const courseUid = this.activatedRoute.snapshot.queryParamMap.get('courseUid');
                const badgeUid = this.activatedRoute.snapshot.queryParamMap.get('badgeUid');
                const playlistUri = this.activatedRoute.snapshot.queryParamMap.get('playlist');
                const cardUri = this.activatedRoute.snapshot.queryParamMap.get('card');
                const isCourseBadgeLaunch = courseUid && !playlistUri && !cardUri && !badgeUid;
                const isDeepLinkingCourseBadgeLaunch = courseUid && badgeUid;

                const assignmentId = this.activatedRoute.snapshot.queryParamMap.get('assignmentId');
                const courseUri = this.activatedRoute.snapshot.queryParamMap.get('courseUri');

                if (assignmentId && courseUri) {
                  const isStaff =
                    this.store.selectSnapshot(UserAuthState.userHasInstructorRole) ||
                    this.store.selectSnapshot(UserAuthState.userHasAdminRole) ||
                    this.store.selectSnapshot(UserAuthState.userHasSuperAdminRole);
                  // `courseUri` arrives decoded from the query param; re-encode both segments so a
                  // uri containing URL-reserved characters can't break router path parsing.
                  const encodedCourseUri = encodeURIComponent(courseUri);
                  const encodedAssignmentId = encodeURIComponent(assignmentId);
                  const target = isStaff
                    ? `/lti/course/setup/${encodedCourseUri}/marking/${encodedAssignmentId}`
                    : `/lti/course/setup/${encodedCourseUri}/assignment-dev-preview/${encodedAssignmentId}`;
                  RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, target);
                  return;
                }

                if (isCourseBadgeLaunch || isDeepLinkingCourseBadgeLaunch) {
                  RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/lti/course/config', {
                    queryParams: {
                      courseUid: courseUid,
                      requestId: requestId,
                    },
                  });
                  return;
                }

                if (playlistUri && !cardUri) {
                  this.playlistDataService.getPlaylistDetails(playlistUri, null, null, null, null).subscribe((data) => {
                    if (data.isSuccess) {
                      RedirectHelper.redirectByParams(
                        this.ngZone,
                        this.router,
                        this.activatedRoute,
                        {
                          playlistUri: playlistUri,
                        },
                        'PLAYLIST',
                      );
                    } else {
                      this.redirectToHomePage();
                    }
                  });
                } else if (cardUri && playlistUri) {
                  if (isSuccess) {
                    RedirectHelper.redirectByParams(
                      this.ngZone,
                      this.router,
                      this.activatedRoute,
                      {
                        playlistUri: playlistUri,
                        resourceUri: cardUri,
                        pageNumberUri: 'page/1',
                      },
                      'RESOURCE',
                    );
                  }
                } else {
                  const destination = this.activatedRoute.snapshot.queryParamMap.get('destination');

                  if (destination) {
                    if (destination === 'skills' || destination === 'folio') {
                      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/folio');
                    } else if (destination === 'submitSkills') {
                      const coursePlaylistUid = this.activatedRoute.snapshot.queryParamMap.get('coursePlaylistUid');
                      RedirectHelper.redirectByUrl(
                        this.ngZone,
                        this.router,
                        this.activatedRoute,
                        `/lti/skills/submit?coursePlaylistUid=${coursePlaylistUid}`,
                      );
                    } else {
                      this.redirectToHomePage();
                    }
                  } else {
                    this.redirectToHomePage();
                  }
                }
              });
          } else {
            RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/lti/registration/error');
          }
        },
        (_) => {
          RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/lti/registration/error');
        },
      );
    } else {
      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/lti/registration/error');
    }
  }

  private saveAdditionalParametersIntoLocalStorage() {
    const ltiProvider = this.activatedRoute.snapshot.queryParamMap.get('provider');
    const courseUid = this.activatedRoute.snapshot.queryParamMap.get('courseUid');
    if (ltiProvider) {
      localStorage.setItem(LTI_PROVIDER, ltiProvider);
    }
    if (courseUid) {
      localStorage.setItem(LTI_COURSE_UID, courseUid);
    }
  }

  private redirectToHomePage() {
    const homePageUri = this.store.selectSnapshot(UserAuthState.homePageUri);
    RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, homePageUri);
  }
}
